/* eslint-disable react/no-danger */

import { Button, RatingStars, useMediaQuery, FlavorIcon, SuccessIcon } from '@ag1/ui';
import { useCallback, useMemo, useState } from 'react';

import { useDate } from '@portal/shared/hooks';
import { V53WrittenReview } from '@portal/types/review';
import { convertUnicodeHexToString } from '@portal/shared/utils/format/convertUnicodeHexToString';
import { decodeHTMLEntities } from '@portal/shared/utils/format/decodeHTMLEntities';
import { escapeRegExp } from '@portal/shared/utils/format/escapeRegExp';
import {
  REVIEW_PRODUCT_SKU_LIST,
  REVIEW_PRODUCT_SKU_TO_PRODUCT_TAG_MAP,
  REVIEW_PRODUCT_SKU_TO_FLAVOR_TAG_MAP,
  REVIEW_PRODUCT_SKU_TO_FLAVOR_COLOR_MAP
} from '@portal/shared/constants/review';
import { useAGZAccount } from '@portal/state/hooks/useAGZAccount';

import { DESKTOP_REVIEW_CONTENT_CHAR_LIMIT, MOBILE_REVIEW_CONTENT_CHAR_LIMIT } from '../constants';
import styles from './ReviewEntry.module.scss';

interface Props extends V53WrittenReview {
  searchTerm: string;
}

export const ReviewEntry = ({
  searchTerm,
  customerName,
  rating,
  title,
  content,
  reviewDate,
  productLabel,
  verifiedPurchase,
  sku
}: Props) => {
  const atXSmall = useMediaQuery('xs');

  const { formattedDateString: formattedReviewDate } = useDate({
    date: reviewDate / 1000,
    fallbackString: 'No Date'
  });

  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const decodedTitle = decodeHTMLEntities(convertUnicodeHexToString(title));
  const decodedContent = decodeHTMLEntities(convertUnicodeHexToString(content));

  // only review content that exceed a char limit is expandable
  const characterLimit = useMemo(
    () => (atXSmall ? DESKTOP_REVIEW_CONTENT_CHAR_LIMIT : MOBILE_REVIEW_CONTENT_CHAR_LIMIT),
    [atXSmall]
  );

  const isContentExpandable = decodedContent.length > characterLimit;

  const { isAgzGateEnabled: enableAGZ } = useAGZAccount();

  const formattedTitle = useMemo(() => {
    // if a search term is involved, append the highlight marks into the string
    // and render the string as HTML
    if (searchTerm.length) {
      const searchTermRegex = new RegExp(escapeRegExp(searchTerm), 'gi');
      const highlightedTitle = decodedTitle.replace(
        searchTermRegex,
        '<mark class="highlight">$&</mark>'
      );
      return <div dangerouslySetInnerHTML={{ __html: highlightedTitle }} />;
    }
    return decodedTitle;
  }, [decodedTitle, searchTerm]);

  const visibleReviewContent = useMemo(() => {
    let visibleContent =
      isContentExpandable && !isContentExpanded
        ? `"${decodedContent.slice(0, characterLimit)}...`
        : `"${decodedContent}"`;

    // if a search term is involved, append the highlight marks into the string
    // and render the string as HTML
    if (searchTerm.length) {
      const searchTermRegex = new RegExp(escapeRegExp(searchTerm), 'gi');
      visibleContent = visibleContent.replace(searchTermRegex, '<mark class="highlight">$&</mark>');
      return <div dangerouslySetInnerHTML={{ __html: visibleContent }} />;
    }

    // if no search term involved, render as plain string
    return visibleContent;
  }, [isContentExpandable, isContentExpanded, decodedContent, characterLimit, searchTerm]);

  const displayableName = useMemo(() => {
    const [firstName, lastName] = customerName.split(' ').slice(0, 2);
    return `${firstName}${lastName ? ` ${lastName.charAt(0)}.` : ''}`;
  }, [customerName]);

  const productTag = useMemo(() => {
    return sku && REVIEW_PRODUCT_SKU_LIST.includes(sku)
      ? REVIEW_PRODUCT_SKU_TO_PRODUCT_TAG_MAP[sku] || productLabel || ''
      : productLabel || '';
  }, [sku, productLabel]);

  const flavorTag = useMemo(() => {
    return sku && REVIEW_PRODUCT_SKU_LIST.includes(sku)
      ? REVIEW_PRODUCT_SKU_TO_FLAVOR_TAG_MAP[sku]
      : '';
  }, [sku]);

  const flavorColor = useMemo(() => {
    return sku && REVIEW_PRODUCT_SKU_LIST.includes(sku)
      ? REVIEW_PRODUCT_SKU_TO_FLAVOR_COLOR_MAP[sku]
      : '';
  }, [sku]);

  const toggleButtonLabel = isContentExpanded ? 'Read Less' : 'Read More';

  const onToggleContentExpansion = useCallback(() => {
    setIsContentExpanded((prev) => !prev);
  }, []);

  return (
    <div className={styles.reviewEntry}>
      <div className={styles.header}>
        <RatingStars rating={rating} thin />
        {productTag.length && <span className={styles.tag}>{productTag}</span>}
      </div>
      <h4 className={styles.title}>{formattedTitle}</h4>
      <div>
        <p className={styles.description}>{visibleReviewContent}</p>
        {isContentExpandable && (
          <div>
            <Button variant="link" size="small" onClick={onToggleContentExpansion}>
              {toggleButtonLabel}
            </Button>
          </div>
        )}
      </div>
      <div className={styles.customerInfo}>
        <span className={styles.name}>{displayableName}</span>
        {verifiedPurchase && (
          <span className={styles.verificationInfo}>
            <SuccessIcon color="#023D3D" size={18} strokeWidth={1} />
            <span className={styles.verifiedCustomer}>Verified Customer</span>
          </span>
        )}
      </div>
      <div className={styles.date}>{formattedReviewDate}</div>
      {enableAGZ && flavorTag && (
        <div className={styles.flavor}>
          <FlavorIcon variant="default" size={26} color={flavorColor} titleAccess={flavorTag} />
          {flavorTag}
        </div>
      )}
    </div>
  );
};
